"use client";
import React from "react";

import {
  Box,
  BoxSx,
  IconNameType,
  Target,
  ThemeColors
} from "../../foundations";
import { Icon } from "../../foundations/Icon/Icon";
import { Text } from "../../foundations/Typography/Text";

export type ChipType =
  | "neutral"
  | "critical"
  | "warning"
  | "success"
  | "info"
  | "brand";

interface ChipStyles {
  backgroundColor: ThemeColors;
  color: ThemeColors;
  ":hover": {
    backgroundColor: ThemeColors;
    color: ThemeColors;
  };
  ":active": {
    backgroundColor: ThemeColors;
    color: ThemeColors;
  };
}

export const chipColors: Record<ChipType, ChipStyles> = {
  neutral: {
    backgroundColor: "chip-surface-neutral",
    color: "chip-onSurface-neutral",
    ":hover": {
      backgroundColor: "chip-surface-neutral",
      color: "chip-onSurface-neutral-hover"
    },
    ":active": {
      backgroundColor: "chip-surface-neutral",
      color: "chip-onSurface-neutral-click"
    }
  },
  critical: {
    backgroundColor: "feedback-surface-critical",
    color: "feedback-onSurface-critical",
    ":hover": {
      backgroundColor: "feedback-surface-critical",
      color: "feedback-onSurface-critical-hover"
    },
    ":active": {
      backgroundColor: "feedback-surface-critical",
      color: "feedback-onSurface-critical-click"
    }
  },
  warning: {
    backgroundColor: "feedback-surface-warning",
    color: "feedback-onSurface-warning",
    ":hover": {
      backgroundColor: "feedback-surface-warning",
      color: "feedback-onSurface-warning-hover"
    },
    ":active": {
      backgroundColor: "feedback-surface-warning",
      color: "feedback-onSurface-warning-click"
    }
  },
  success: {
    backgroundColor: "feedback-surface-success",
    color: "feedback-onSurface-success",
    ":hover": {
      backgroundColor: "feedback-surface-success",
      color: "feedback-onSurface-success-hover"
    },
    ":active": {
      backgroundColor: "feedback-surface-success",
      color: "feedback-onSurface-success-click"
    }
  },
  info: {
    backgroundColor: "feedback-surface-info",
    color: "feedback-onSurface-info",
    ":hover": {
      backgroundColor: "feedback-surface-info",
      color: "feedback-onSurface-info-hover"
    },
    ":active": {
      backgroundColor: "feedback-surface-info",
      color: "feedback-onSurface-info-click"
    }
  },
  brand: {
    backgroundColor: "chip-surface-brand",
    color: "chip-onSurface-brand",
    ":hover": {
      backgroundColor: "chip-surface-brand",
      color: "chip-onSurface-brand-hover"
    },
    ":active": {
      backgroundColor: "chip-surface-brand",
      color: "chip-onSurface-brand-click"
    }
  }
};

export type ChipProps = React.ComponentProps<typeof Target> & {
  label: string;
  icon?: IconNameType;
  size?: "medium" | "large";
  variant?: ChipType;
  onDismiss?: () => void;
};

export const Chip: React.FC<ChipProps> = (props) => {
  const {
    label,
    size = "large",
    variant = "neutral",
    icon,
    onDismiss,
    disabled = false
  } = props;

  const layoutStyles: BoxSx = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "spacing-2",
    width: "max-content"
  };

  const appearanceStyles: BoxSx = React.useMemo(
    () => ({
      borderRadius: "radius-round",
      padding: size === "large" ? "spacing-3" : "spacing-2",
      height: size === "large" ? "spacing-8" : "spacing-7"
    }),
    [size]
  );

  const textVariant = React.useMemo(() => {
    return size === "large" ? "body-2-medium" : "caption";
  }, [size]);

  const colorStyles = React.useMemo<BoxSx>(
    () => ({
      ...chipColors[variant],
      transition: "transition-color-medium",
      ":focus-visible": {
        outline: "2px solid",
        outlineColor: "focus",
        outlineOffset: "2px"
      }
    }),
    [variant]
  );

  const disabledStyles = React.useMemo<BoxSx>(() => {
    if (disabled) {
      return {
        backgroundColor: "surface-neutral-secondary",
        color: "text-input-disabled",
        ":hover": {
          backgroundColor: "surface-neutral-secondary"
        },
        ":active": {
          backgroundColor: "surface-neutral-secondary"
        },
        ":focus-visible": {
          backgroundColor: "surface-neutral-secondary"
        }
      };
    }
    return {};
  }, [disabled]);

  return (
    <Box
      tabIndex={0}
      sx={{
        ...colorStyles,
        ...layoutStyles,
        ...disabledStyles,
        ...appearanceStyles
      }}
      disabled={disabled}
    >
      {icon && <Icon size="xxs" color="currentColor" icon={icon} />}
      <Text variant={textVariant} weight="medium">
        {label}
      </Text>
      {onDismiss && (
        <Target
          sx={{ ...colorStyles, marginTop: "spacing-2" }}
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <Icon color="currentColor" size="xxs" icon="x" />
        </Target>
      )}
    </Box>
  );
};
